// src/store/slices/authslice.js
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  signOut
} from 'firebase/auth';
import { 
  doc, 
  getDoc, 
  setDoc, 
  collection, 
  query, 
  where, 
  getDocs,
  onSnapshot
} from 'firebase/firestore';
import { auth, db } from '../../firebase';

// Helper function to generate email
const generateEmail = (identifier) => {
  const cleanIdentifier = identifier.toLowerCase().replace(/[^a-z0-9]/g, '');
  return `${cleanIdentifier}@grillshack.com`;
};

export const signUp = createAsyncThunk(
  'auth/signUp',
  async ({ username, password, phone, address, location }, { rejectWithValue }) => {
    try {
      // Check if username or phone already exists
      const usernameQuery = query(collection(db, 'customers'), where('username', '==', username));
      const phoneQuery = query(collection(db, 'customers'), where('phone', '==', phone));
      
      const [usernameSnapshot, phoneSnapshot] = await Promise.all([
        getDocs(usernameQuery),
        getDocs(phoneQuery)
      ]);

      if (!usernameSnapshot.empty) {
        throw new Error('Username already exists');
      }
      if (!phoneSnapshot.empty) {
        throw new Error('Phone number already registered');
      }

      const email = generateEmail(username);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Store the user's details along with their selected delivery location only
      await setDoc(doc(db, 'customers', user.uid), {
        username,
        phone,
        address,
        email,
        location,     // Store only the delivery location
      });

      return {
        uid: user.uid,
        username,
        phone,
        address,
        location,
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const login = createAsyncThunk(
  'auth/login',
  async ({ identifier, password }, { rejectWithValue, dispatch }) => {
    try {
      const identifierQuery = query(
        collection(db, 'customers'),
        where(
          identifier.includes('@') ? 'email' : 
          /^\d+$/.test(identifier) ? 'phone' : 'username', 
          '==', identifier
        )
      );
      
      const querySnapshot = await getDocs(identifierQuery);
      
      if (querySnapshot.empty) {
        throw new Error('User not found');
      }

      const userData = querySnapshot.docs[0].data();
      const email = userData.email;

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      const ordersQuery = query(collection(db, 'orders'), where('customerId', '==', userCredential.user.uid));
      const ordersSnapshot = await getDocs(ordersQuery);
      const userOrders = ordersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      dispatch(setCustomerOrders(userOrders));
      
      return {
        uid: userCredential.user.uid,
        username: userData.username,
        phone: userData.phone,
        address: userData.address,
        location: userData.location, // Return the delivery location
      };
    } catch (error) {
      return rejectWithValue('Invalid credentials');
    }
  }
);

export const fetchUserData = createAsyncThunk(
  'auth/fetchUserData',
  async (uid, { dispatch }) => {
    const userDoc = await getDoc(doc(db, 'customers', uid));
    if (!userDoc.exists()) {
      throw new Error('User data not found');
    }

    const userData = userDoc.data();
    
    // Fetch orders
    const ordersQuery = query(collection(db, 'orders'), where('customerId', '==', uid));
    const ordersSnapshot = await getDocs(ordersQuery);
    const userOrders = ordersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    dispatch(setCustomerOrders(userOrders));

    return {
      uid,
      username: userData.username,
      phone: userData.phone,
      address: userData.address,
      location: userData.location, // Include delivery location in fetched data
    };
  }
);

export const signOutUser = createAsyncThunk(
  'auth/signOut',
  async (_, { dispatch }) => {
    await signOut(auth);
    dispatch(clearUser());
  }
);

export const placeOrder = createAsyncThunk(
  'orders/placeOrder',
  async ({ orderData, deliveryMethod }, { getState, rejectWithValue }) => {
    try {
      const { user, currentOrderDeliveryMethod } = getState().auth;
      if (!user?.uid) {
        throw new Error('User not authenticated');
      }

      const orderId = crypto.randomUUID();
      const orderRef = doc(db, 'orders', orderId);

      const order = {
        id: orderId, // Added so that our UI (e.g. OrderCard) can use order.id
        orderId,
        customerId: user.uid,
        customerName: user.username,
        customerPhone: user.phone || 'Not Provided',
        contactPhone: deliveryMethod.contactPhone || user.phone,
        useDifferentPhone: deliveryMethod.useDifferentPhone || false,
        deliveryMethod: deliveryMethod.type || currentOrderDeliveryMethod,
        // Use the provided (different) address if available; otherwise use the user's saved address.
        deliveryAddress: deliveryMethod.type === 'delivery'
          ? (deliveryMethod.deliveryAddress || user.address)
          : null,
        differentPayer: deliveryMethod.differentPayer || false,
        payerName: deliveryMethod.payerName || '',
        paymentReference: deliveryMethod.paymentReference,
        items: orderData.items,
        totalAmount: orderData.totalAmount,
        status: 'pending',
        createdAt: new Date().toISOString(),
      };

      await setDoc(orderRef, order);

      return order;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const setupOrdersListener = createAsyncThunk(
  'auth/setupOrdersListener',
  async (userId, { dispatch }) => {
    const ordersRef = query(collection(db, 'orders'), where('customerId', '==', userId));
    const unsubscribe = onSnapshot(ordersRef, (snapshot) => {
      const ordersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      dispatch(authSlice.actions.setCustomerOrders(ordersData));
    });
    return unsubscribe;
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    isAuthenticated: false,
    user: null,
    loading: false,
    error: null,
    orders: [],
    currentOrderDeliveryMethod: 'pick-up',
    listenerUnsubscribe: null,
  },
  reducers: {
    setCustomerOrders: (state, action) => {
      state.orders = action.payload;
    },
    clearUser: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.orders = [];
      state.currentOrderDeliveryMethod = 'pick-up';
      if (state.listenerUnsubscribe) {
        state.listenerUnsubscribe();
      }
      state.listenerUnsubscribe = null;
    },
    updateUserProfile: (state, action) => {
      const { username, phone, address } = action.payload;
      if (username) state.user.username = username;
      if (phone) state.user.phone = phone;
      if (address) state.user.address = address;
    },
    setDeliveryMethod: (state, action) => {
      state.currentOrderDeliveryMethod = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signUp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signUp.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
      })
      .addCase(signUp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      })
      .addCase(placeOrder.pending, (state) => {
        state.loading = true;
      })
      .addCase(placeOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.orders.push(action.payload);
      })
      .addCase(placeOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchUserData.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUserData.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
      })
      .addCase(fetchUserData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      })
      .addCase(setupOrdersListener.fulfilled, (state, action) => {
        state.listenerUnsubscribe = action.payload;
      })
      .addCase(setupOrdersListener.rejected, (state, action) => {
        state.error = action.error.message;
      });
  }, 
});

export const { setCustomerOrders, clearUser, updateUserProfile, setDeliveryMethod } = authSlice.actions;
export default authSlice.reducer;
